import type { SpinResult } from '@aerofruits/game-sdk';

export interface LightningNumberSlot {
  number: number;
  tierId: string;
  tenths: number;
}

export interface LightningNumberRoundDetail {
  selectedNumber: number;
  dice: number[];
  matchCount: number;
  lightningNumberCount: number;
  lightningNumbers: number[];
  lightningSlots: LightningNumberSlot[];
  selectedIsLightning: boolean;
  appliedLightningMultiplierTenths: number;
  baseProfitMultiplierTenths: number;
  lightningProfitMultiplierTenths: number;
  profitMultiplierTenths: number;
  stakeReturnMultiplierTenths: number;
  totalPayoutMultiplierTenths: number;
  profitAmountMinor: number;
  stakeReturnAmountMinor: number;
  totalPayoutAmountMinor: number;
}

export function parseLightningNumberResult(result: SpinResult): LightningNumberRoundDetail | null {
  const block = result.lightning_number_dice;
  const dice = block?.dice ?? result.dice;
  if (!dice || dice.length !== 3) return null;
  const selectedNumber = block?.selected_number ?? result.selected_number ?? 0;
  const matchCount = block?.match_count ?? result.match_count ?? 0;
  const slots = (block?.lightning_slots ?? []).map((s) => ({
    number: s.number,
    tierId: s.tier_id,
    tenths: s.tenths,
  }));
  return {
    selectedNumber,
    dice: [...dice],
    matchCount,
    lightningNumberCount: block?.lightning_number_count ?? result.lightning_number_count ?? slots.length,
    lightningNumbers: [...(block?.lightning_numbers ?? result.lightning_numbers ?? [])],
    lightningSlots: slots,
    selectedIsLightning: block?.selected_is_lightning ?? result.selected_is_lightning ?? false,
    appliedLightningMultiplierTenths:
      block?.applied_lightning_multiplier_tenths ?? result.applied_lightning_multiplier_tenths ?? 0,
    baseProfitMultiplierTenths:
      block?.base_profit_multiplier_tenths ?? result.base_profit_multiplier_tenths ?? 0,
    lightningProfitMultiplierTenths:
      block?.lightning_profit_multiplier_tenths ?? result.lightning_profit_multiplier_tenths ?? 0,
    profitMultiplierTenths: block?.profit_multiplier_tenths ?? result.profit_multiplier_tenths ?? 0,
    stakeReturnMultiplierTenths:
      block?.stake_return_multiplier_tenths ?? result.stake_return_multiplier_tenths ?? 0,
    totalPayoutMultiplierTenths:
      block?.total_payout_multiplier_tenths ?? result.total_payout_multiplier_tenths ?? 0,
    profitAmountMinor: block?.profit_amount_minor ?? result.profit_amount_minor ?? 0,
    stakeReturnAmountMinor: block?.stake_return_amount_minor ?? result.stake_return_amount_minor ?? 0,
    totalPayoutAmountMinor:
      block?.total_payout_amount_minor ??
      result.total_payout_amount_minor ??
      result.payout_minor ??
      result.total_win_minor,
  };
}

/** Formats a multiplier from tenths (e.g. 15 → x1.5, 390 → x39). Drops trailing .0. */
export function formatMultiplierTenths(tenths: number): string {
  const value = tenths / 10;
  if (Number.isInteger(value)) return `x${value}`;
  const trimmed = parseFloat(value.toFixed(1));
  return Number.isInteger(trimmed) ? `x${trimmed}` : `x${trimmed.toFixed(1)}`;
}

export function formatLightningMultiplierTenths(tenths: number): string {
  return formatMultiplierTenths(tenths);
}
